// ---------------------------------------------------------------------------
// V20 — THE LIVE OPS CONSOLE.
//
// `02-module-catalogue.md` D2: "Event-day command: who is in, who is late, which posts are
// dark." `01a-competitor-teardown.md` §B3 is the whole brief for how it behaves: "Do not
// ship a status page that needs a manual refresh." The vendor documents that as a
// characteristic of theirs, and it is the exact surface that must be live.
//
// ---------------------------------------------------------------------------
// THIS SCREEN JUDGES NOTHING ABOUT THE WORK
//
// Dark, short, late and how late all arrive from `app.live_board`. The reason is in
// `0054`'s header and it is not tidiness: this same question will be asked by V24's client
// view, and two places computing "is this post dark" is two answers, one of which is wrong
// on matchday. What this file decides is colour, order of reading, and what to say when
// there is nothing to show.
//
// ---------------------------------------------------------------------------
// THE POLL, AND THE THREE THINGS THAT MAKE IT SAFE
//
//   1. **It is paced by completion, not by the wall clock.** A `setInterval` fires whether
//      or not the previous request came back, so a slow reply on a stadium wifi becomes a
//      queue of overlapping requests that never drains. This schedules the next poll when
//      the last one finishes.
//   2. **It stops when the tab is hidden.** A console left open on a laptop lid for a week
//      is otherwise twenty thousand requests about an event that finished. `visibilitychange`
//      also gives the refresh-on-return that a person expects.
//   3. **The change log is polled with a CURSOR** and the board whole. The board is a fixed
//      number of rows; the log is not, and re-reading it every three seconds would be a
//      self-inflicted load problem on the busiest surface in the product.
//
// §V20's verify line asks that a check-in show "within 5 seconds". Three seconds satisfies
// it with two ordinary GETs and no socket — see the route file for why not a socket.
// ---------------------------------------------------------------------------

import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import { send } from "../api/auth";
import { console_ } from "../api/console";
import { LoadFailed } from "../design/LoadFailed";
import { StatusChip } from "../design/StatusChip";
import { translateLabel } from "../i18n/translate";
import { useT } from "../i18n/LocaleProvider";
import type { Translate } from "../i18n/translate";
import { Headcount } from "./Headcount";
import { RollCall } from "./RollCall";
import { Tabs } from "../design/Tabs";
import { useSession } from "../session/SessionProvider";
import type { Failure } from "../system/failure";
import { clock } from "../system/format";

const POLL_MS = 3000;

type Post = {
  requirement_id: string;
  zone: string | null;
  role_key: string;
  role_name: string;
  criticality: number;
  starts_at: string;
  ends_at: string;
  due_at: string;
  headcount: number;
  booked: number;
  present: number;
  departed: number;
  late: number;
  worst_minutes_late: number;
  is_dark: boolean;
  records_presence: boolean;
};

type BoardWindow = {
  opens_at: string | null;
  closes_at: string | null;
  is_open: boolean;
  opens_in_minutes: number | null;
};

type Summary = {
  posts: number;
  dark: number;
  short: number;
  present: number;
  booked: number;
  late: number;
  not_recording_presence: number;
};

type Board = { window: BoardWindow; posts: Post[]; summary: Summary };

type Change = {
  occurred_at: string;
  source: string;
  actor_kind: string;
  actor_user_account_id: string | null;
  actor_role: string | null;
  action: string;
  subject_type: string;
  subject_id: string;
  reason_code: string | null;
  detail: string | null;
};

function hhmm(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : clock(iso);
}

/**
 * What a post's state is called, in the words a supervisor would use.
 *
 * THE ORDER OF THESE TESTS IS THE PRODUCT DECISION. Dark first, because it is the only one
 * that means nobody is there; then short, because somebody is but not enough of them; then
 * late, which can be true of a post that is otherwise fine. A post that is both dark and
 * late reads as dark — the lateness is implied and saying both would dilute the one word
 * that matters.
 */
// THE TONES ARE THE DESIGN SYSTEM'S FOUR AND NOT A SET THIS FILE CHOOSES. The positive one
// is `live`, not `ok` — the compiler said so, which is what a token vocabulary expressed as
// a union type is for.
type Tone = "live" | "attention" | "failed" | "neutral";

function postState(p: Post, t: Translate): { tone: Tone; label: string } {
  // A POST THAT RECORDS NO PRESENCE IS NOT A PROBLEM AND MUST NOT LOOK LIKE ONE. 0047's
  // nullable policy: remote and asynchronous work records nothing, so its emptiness means
  // nothing, and a permanent red tile is a red tile nobody reads.
  if (!p.records_presence) return { tone: "neutral", label: t("No check-in") };
  if (p.is_dark) return { tone: "failed", label: t("Dark") };
  if (p.present < p.booked) return { tone: "attention", label: t("{present} of {booked} here", { present: p.present, booked: p.booked }) };
  if (p.late > 0) return { tone: "attention", label: t("{n} late", { n: p.late }) };
  if (p.booked < p.headcount) return { tone: "attention", label: t("{booked} of {headcount} booked", { booked: p.booked, headcount: p.headcount }) };
  return { tone: "live", label: t("All present") };
}

export function LiveOps() {
  const { id = "" } = useParams();
  const { apiBase, me } = useSession();
  const { locale, t, refusal } = useT();
  // S12. The roll call is offered to anybody who may record attendance; whether they may
  // record it for THIS person is the database's question (may_record_attendance_for).
  const mayRecord = me?.authenticated === true && me.permissions.includes("checkin.record");
  const [rollCall, setRollCall] = useState<string | null>(null);
  const [allChanges, setAllChanges] = useState(false);

  const [board, setBoard] = useState<Board | null>(null);
  const [changes, setChanges] = useState<Change[]>([]);
  const [failed, setFailed] = useState<string | null>(null);
  // The first read's failure, for the screen that never got a board (later failures keep the board, above).
  const [firstFailed, setFirstFailed] = useState<Failure | null>(null);
  const [lastAt, setLastAt] = useState<string | null>(null);

  // THE CURSOR LIVES IN A REF, NOT IN STATE. It is read inside the poll and written by it;
  // as state it would be a dependency of the callback, so every poll would rebuild the
  // callback and restart the loop — which is the bug where a polling screen quietly doubles
  // its request rate on every tick.
  const cursor = useRef<string | null>(null);
  const alive = useRef(true);

  const poll = useCallback(async () => {
    const [b, c] = await Promise.all([
      send<{ board?: Board; error?: { message?: string } }>(console_.liveBoard(apiBase, id)),
      send<{ change_log?: { cursor: string | null; entries: Change[] } }>(
        console_.changeLog(apiBase, id, cursor.current),
      ),
    ]);
    if (!alive.current) return;

    if (b.status === 200 && b.body?.board) {
      setBoard(b.body.board);
      setFailed(null);
      setFirstFailed(null);
      setLastAt(new Date().toISOString());
    } else if (b.status === 404) {
      setFailed(t("That event is not one of yours."));
      setFirstFailed({ status: 404, refusal: t("That event is not one of yours.") });
    } else {
      // THE PREVIOUS BOARD STAYS ON SCREEN. A blank grid during a dropped request would
      // read as "every post is empty", which on this screen is the worst possible lie —
      // and stadium wifi drops requests constantly. The staleness is shown instead.
      setFailed(refusal(b.body?.error) ?? t("The board could not be refreshed."));
      setFirstFailed({ status: b.status, refusal: refusal(b.body?.error) });
    }

    if (c.status === 200 && c.body?.change_log) {
      const { cursor: next, entries } = c.body.change_log;
      if (entries.length > 0) {
        // NEWEST FIRST FROM THE SERVER, so new entries go on the front. Capped, because a
        // long event with a busy roster would otherwise grow this list without limit in a
        // tab nobody reloads.
        setChanges((prev) => [...entries, ...prev].slice(0, 200));
      }
      if (next) cursor.current = next;
    }
  }, [apiBase, id]);

  useEffect(() => {
    alive.current = true;
    let timer: number | undefined;

    // PACED BY COMPLETION. See the header: setInterval would stack requests behind a slow
    // reply and never drain.
    const tick = async () => {
      if (!alive.current) return;
      if (document.visibilityState === "visible") {
        try {
          await poll();
        } catch {
          // A thrown fetch is a dropped connection, which on this surface is expected
          // rather than exceptional. The next tick tries again; the screen keeps the last
          // board and says when it was.
        }
      }
      if (alive.current) timer = window.setTimeout(tick, POLL_MS);
    };
    void tick();

    // AND A POLL ON RETURN, so somebody who opens the laptop sees the truth immediately
    // rather than after the interval.
    const onVisible = () => {
      if (document.visibilityState === "visible") void poll();
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      alive.current = false;
      if (timer !== undefined) window.clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [poll]);

  if (board === null && firstFailed !== null) {
    return (
      <section className="panel">
        <div className="panel-head">
          <h2>{t("Live ops")}</h2>
        </div>
        <LoadFailed failure={firstFailed} fallback={t("The board could not be refreshed.")} onRetry={() => void poll()} />
      </section>
    );
  }
  if (board === null) {
    return (
      <section className="panel">
        <div className="panel-head">
          <h2>{t("Live ops")}</h2>
        </div>
        <p className="muted">{t("Reading the board…")}</p>
      </section>
    );
  }

  const w = board.window;

  return (
    <div className="stack">
      <section className="panel">
        <div className="panel-head">
          <h2>{t("Live ops")}</h2>
          {/* WHEN IT LAST REFRESHED, ALWAYS VISIBLE. §B3's complaint is about a page that
              needs a manual refresh; the other half of that promise is that a page which
              STOPPED refreshing has to admit it, or it is worse than one that never
              claimed to. */}
          <p className="muted">
            {failed
              ? t("Last updated {when} — {why}", { when: lastAt ? hhmm(lastAt) : "—", why: failed })
              : t("Updated {when}, every {n}s", { when: lastAt ? hhmm(lastAt) : "—", n: POLL_MS / 1000 })}
          </p>
        </div>

        {/* THE WINDOW, SAID IN WORDS. An event that has not started shows a grid of unlit
            posts, and without this sentence somebody reads that as a fault. */}
        {!w.is_open ? (
          <StatusChip
            tone="neutral"
            label={
              w.opens_in_minutes !== null
                ? t("This event is not running yet — the operational window opens in {n} minutes", { n: w.opens_in_minutes })
                : t("The operational window for this event has closed")
            }
          />
        ) : null}

        {/* Tour T-57 (owner): the counts are tiles, a number over its word; a tile with something
            wrong in it is amber (a word as well as a colour: the word is the tile's own label). */}
        <div className="stat-tiles">
          <div className="stat-tile">
            <strong className="numeric">{t("{present} of {booked}", { present: board.summary.present, booked: board.summary.booked })}</strong>
            <span>{t("Present")}</span>
          </div>
          <div className="stat-tile">
            <strong className="numeric">{board.summary.dark}</strong>
            <span>{t("Dark")}</span>
          </div>
          <div className={`stat-tile${board.summary.short > 0 ? " is-attention" : ""}`}>
            <strong className="numeric">{board.summary.short}</strong>
            <span>{t("Short")}</span>
          </div>
          <div className={`stat-tile${board.summary.late > 0 ? " is-attention" : ""}`}>
            <strong className="numeric">{board.summary.late}</strong>
            <span>{t("Late")}</span>
          </div>
          {board.summary.not_recording_presence > 0 ? (
            <div className="stat-tile">
              <strong className="numeric">{board.summary.not_recording_presence}</strong>
              <span>{t("Record no check-in")}</span>
            </div>
          ) : null}
        </div>
      </section>

      <Tabs
        label={t("Live ops")}
        tabs={[
          {
            id: "live-now",
            label: t("Now"),
            content: (
              <div className="stack">
                <section className="panel">
                  <div className="panel-head">
                    <h2>{t("Posts")}</h2>
                    {/* THE ORDER COMES FROM THE SERVER: most critical first. A dark medic post and a
                        dark leaflet post are not the same emergency — §B2 — and re-sorting here would
                        be the second answer this slice exists to avoid. */}
                    <p className="muted">{t("Most critical first")}</p>
                  </div>
                  {board.posts.length === 0 ? (
                    <p className="muted">{t("This event has no posts yet.")}</p>
                  ) : (
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>{t("Post")}</th>
                          <th>{t("Due")}</th>
                          <th>{t("Here")}</th>
                          <th>{t("State")}</th>
                          <th>{t("Worst delay")}</th>
                          {mayRecord ? <th>&nbsp;</th> : null}
                        </tr>
                      </thead>
                      <tbody>
                        {board.posts.map((p) => {
                          const s = postState(p, t);
                          return (
                            <Fragment key={p.requirement_id}>
                            <tr>
                              <td>
                                <strong>{p.zone ?? translateLabel(locale, p.role_name)}</strong>
                                <div className="muted">{translateLabel(locale, p.role_name)}</div>
                              </td>
                              <td>{hhmm(p.due_at)}</td>
                              <td>
                                {p.present} / {p.booked}
                                {p.departed > 0 ? <div className="muted">{t("{n} left", { n: p.departed })}</div> : null}
                              </td>
                              <td><StatusChip tone={s.tone} label={s.label} /></td>
                              <td>
                                {p.records_presence && p.worst_minutes_late > 0
                                  ? t("{n} min", { n: p.worst_minutes_late })
                                  : "—"}
                              </td>
                              {mayRecord ? (
                                <td>
                                  {p.records_presence ? (
                                    <button
                                      type="button"
                                      className="btn btn-quiet"
                                      onClick={() => setRollCall(rollCall === p.requirement_id ? null : p.requirement_id)}
                                    >
                                      {t("Roll call")}
                                    </button>
                                  ) : null}
                                </td>
                              ) : null}
                            </tr>
                            {rollCall === p.requirement_id ? (
                              <tr>
                                <td colSpan={6}>
                                  <RollCall requirementId={p.requirement_id} onRecorded={poll} />
                                </td>
                              </tr>
                            ) : null}
                            </Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </section>

                {mayRecord ? <Headcount eventId={id} /> : null}
              </div>
            ),
          },
          {
            id: "live-log",
            label: t("Change log"),
            content: (
              <div className="stack">
                <section className="panel">
                  <div className="panel-head">
                    <h2>{t("Change log")}</h2>
                    <p className="muted">{t("Everything that happened during the operational window")}</p>
                  </div>
                  {changes.length === 0 ? (
                    <p className="muted">{t("Nothing has changed during the operational window.")}</p>
                  ) : (
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>{t("When")}</th>
                          <th>{t("What")}</th>
                          <th>{t("Why")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(allChanges ? changes : changes.slice(0, 25)).map((c) => (
                          <tr key={`${c.occurred_at}-${c.subject_id}-${c.action}`}>
                            <td>{hhmm(c.occurred_at)}</td>
                            <td>
                              {c.action}
                              <div className="muted">{c.subject_type}</div>
                            </td>
                            <td>
                              {/* THE REASON CODE'S OWN ID, NOT A SENTENCE THIS FILE WRITES. Its label
                                  lives in app.assignment_transition_reason; mapping it here would be a
                                  vocabulary in the browser, which absolute rule 4 forbids. A dash
                                  where there is none is honest — most changes have no reason and
                                  D7 only requires one for the five edges that end a booking. */}
                              {c.reason_code ?? c.detail ?? "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                  {changes.length > 25 ? (
                    <div className="row">
                      <button type="button" className="btn btn-quiet" onClick={() => setAllChanges(!allChanges)}>
                        {allChanges ? t("Show fewer") : t("Show all ({n})", { n: changes.length })}
                      </button>
                    </div>
                  ) : null}
                </section>
              </div>
            ),
          },
        ]}
      />
    </div>
  );
}
